import { useCallback } from 'react'
import { FontUpdate } from 'client/components/core/Sidebar/components/Settings/components/Font/BaseFont'
import { getInheritedFontTypeLabel } from 'client/components/core/Sidebar/components/Settings/components/Font/BaseFont'
import { usePage } from 'client/store'
import { pageSelectors } from 'client/store/page/pageSelectors'

export const useFontSelector = <
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  Object extends Record<string, any>,
  FontFamilyKey extends keyof Object,
  FontWeightKey extends keyof Object,
  FontStyleKey extends keyof Object,
  FontFileIdKey extends keyof Object,
>({
  object,
  fontFamilyKey,
  fontWeightKey,
  fontStyleKey,
  fontFileIdKey,
  updateObject,
}: {
  object: Object
  fontFamilyKey: FontFamilyKey
  fontWeightKey: FontWeightKey
  fontStyleKey: FontStyleKey
  fontFileIdKey: FontFileIdKey
  updateObject: (object: Object) => void
}) => {
  const pageType = usePage(pageSelectors.getPageType)
  const parentGlobalSettings = usePage(pageSelectors.getParentGlobalSettings)

  let inheritedFontTypeLabel = getInheritedFontTypeLabel(pageType)
  if (parentGlobalSettings && Object.keys(parentGlobalSettings).length === 0) {
    inheritedFontTypeLabel = 'settings_styles.font_family.inherited'
  }

  const updateObjectFont = useCallback(
    (updatedFont: FontUpdate) => {
      const updatedObject = { ...object }
      const newFontProperties: Partial<Object> = {}
      if (updatedFont.fontFileId) {
        newFontProperties[fontFamilyKey] =
          updatedFont.fontFamily as Object[FontFamilyKey]
        newFontProperties[fontFileIdKey] =
          updatedFont.fontFileId as Object[FontFileIdKey]
        delete updatedObject[fontStyleKey]
        delete updatedObject[fontWeightKey]
      } else {
        newFontProperties[fontFamilyKey] =
          updatedFont.fontFamily as Object[FontFamilyKey]
        if (updatedFont.fontFamily === 'inherit') {
          delete updatedObject[fontStyleKey]
          delete updatedObject[fontWeightKey]
        } else {
          if (updatedFont.fontStyle) {
            newFontProperties[fontStyleKey] =
              updatedFont.fontStyle as Object[FontStyleKey]
          }
          if (updatedFont.fontWeight) {
            newFontProperties[fontWeightKey] =
              updatedFont.fontWeight as Object[FontWeightKey]
          }
        }
        if (updatedObject[fontFileIdKey]) {
          delete updatedObject[fontFileIdKey]
        }
      }
      updateObject({
        ...updatedObject,
        ...newFontProperties,
      })
    },
    [
      object,
      fontFamilyKey,
      fontFileIdKey,
      fontStyleKey,
      fontWeightKey,
      updateObject,
    ],
  )

  return {
    inheritedFontTypeLabel,
    updateObjectFont,
  }
}
